/**
 * Price-series resampling for Hedge Lab focused view (Steve directive 2026-05-09).
 * Daily / Weekly / Monthly toggle drives all three sub-charts (price, RSI, momentum).
 * Bloomberg convention: weekly = Friday close (or last trading day of the ISO week),
 * monthly = last trading day of the calendar month.
 */

export type Period = 'D' | 'W' | 'M';

export interface PricePoint {
  date: string;
  /** OHLC fields are populated from FMP `historical-price-eod/full`. Null for
   *  legacy callers that only need close. */
  open?: number | null;
  high?: number | null;
  low?: number | null;
  close: number;
}

/** Group key used for bucketing. Daily → date itself; weekly → year + ISO week;
 *  monthly → year + month. The LAST observation in each bucket is kept (most
 *  recent close of the period). */
function bucketKey(iso: string, period: Period): string {
  if (period === 'D') return iso;
  if (period === 'M') return iso.slice(0, 7); // YYYY-MM
  // ISO week: Thursday of the week determines the week-year. Standard JS Date
  // gives us enough to compute ISO week without a date library.
  const d = new Date(iso + 'T12:00:00Z');
  // Thursday-anchored ISO week algorithm
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = target.valueOf();
  target.setUTCMonth(0, 1);
  if (target.getUTCDay() !== 4) {
    target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
  }
  const week = 1 + Math.ceil((firstThursday - target.valueOf()) / 604_800_000);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

/**
 * Resample a daily OHLC series to weekly / monthly bars. For OHLC fields:
 *   - open  = first open in the bucket
 *   - high  = max high in the bucket
 *   - low   = min low in the bucket
 *   - close = last close in the bucket
 * (standard exchange convention)
 *
 * If only close is present (legacy daily-prices-cache), open/high/low fall back
 * to close so the bar collapses to a flat tick instead of breaking.
 *
 * Daily mode: pass-through.
 */
export function resamplePrices(prices: PricePoint[], period: Period): PricePoint[] {
  if (period === 'D' || prices.length === 0) return prices;
  const buckets = new Map<string, PricePoint>();
  for (const p of prices) {
    const key = bucketKey(p.date, period);
    const existing = buckets.get(key);
    if (!existing) {
      buckets.set(key, { ...p });
    } else {
      // Aggregate: keep first open, max high, min low, last close+date
      const newHigh = Math.max(existing.high ?? existing.close, p.high ?? p.close);
      const newLow = Math.min(existing.low ?? existing.close, p.low ?? p.close);
      buckets.set(key, {
        date: p.date,
        open: existing.open ?? existing.close,
        high: newHigh,
        low: newLow,
        close: p.close,
      });
    }
  }
  return [...buckets.values()].sort((a, b) => a.date.localeCompare(b.date));
}
